if (!customElements.get('quick-add-variants')) {
  customElements.define(
    'quick-add-variants',
    class QuickAddVariants extends HTMLElement {
      constructor() {
        super();
        this.variants = null;
        this.optionNames = null;
        this.selectedOptions = {};
        this.currentStep = 0;
        this._initialized = false;
      }

      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this.optionGroups = this.querySelectorAll('.qav__group');
        this.optionButtons = this.querySelectorAll('.qav__opt');

        this._parseData();

        // Multi-option: show only first group
        if (this.optionNames.length > 1) {
          this.optionGroups.forEach((g, i) => {
            if (i > 0) g.classList.add('is-hidden');
          });
        }

        this._bind();
      }

      _parseData() {
        try {
          this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
          this.optionNames = JSON.parse(this.querySelector('[data-option-names]').textContent);
        } catch (e) {
          this.variants = [];
          this.optionNames = [];
        }
      }

      _bind() {
        // Mobile: first tap toggles active
        this.addEventListener('click', (e) => {
          if (e.target.closest('.qav__opt')) return;
          if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
            this.classList.toggle('is-active');
          }
        });

        // Close on outside click (mobile)
        document.addEventListener('click', (e) => {
          if (!this.contains(e.target)) {
            this.classList.remove('is-active');
            this._reset();
          }
        });

        // Option clicks
        this.optionButtons.forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            if (btn.classList.contains('qav__opt--out')) return;
            this._onPick(btn);
          });
        });

        // Desktop: reset on mouse leave
        this.addEventListener('mouseleave', () => {
          this._reset();
        });
      }

      _onPick(btn) {
        const pos = btn.dataset.pos;
        const val = btn.dataset.val;
        this.selectedOptions[pos] = val;

        if (Object.keys(this.selectedOptions).length === this.optionNames.length) {
          const variant = this.variants.find((v) =>
            this.optionNames.every((_, i) => v.options[i] === this.selectedOptions[String(i + 1)])
          );
          if (variant && variant.available) this._addToCart(variant);
        } else {
          this.currentStep++;
          this._showStep(this.currentStep);
        }
      }

      _showStep(step) {
        this.optionGroups.forEach((g, i) => g.classList.toggle('is-hidden', i !== step));
        this._markUnavailable();
      }

      _markUnavailable() {
        const group = this.optionGroups[this.currentStep];
        if (!group) return;
        group.querySelectorAll('.qav__opt').forEach((btn) => {
          const test = { ...this.selectedOptions, [btn.dataset.pos]: btn.dataset.val };
          const ok = this.variants.some((v) => {
            for (const [p, val] of Object.entries(test)) {
              if (v.options[parseInt(p) - 1] !== val) return false;
            }
            return v.available;
          });
          btn.classList.toggle('qav__opt--out', !ok);
        });
      }

      // -------------------------------------------------------
      // Cart logic — mirrors product-form.js onSubmitHandler
      // -------------------------------------------------------
      _addToCart(variant) {
        this.classList.remove('is-active');
        this.classList.add('is-loading');

        // Fresh cart reference every time (same as product-form.js)
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData();
        formData.append('id', variant.id);
        formData.append('quantity', '1');

        if (cart) {
          formData.append(
            'sections',
            cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              // Error — same event as product-form.js
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.classList.remove('is-loading');
              return;
            }

            // No cart element → redirect to cart page (theme setting: cart page)
            if (!cart) {
              window.location = window.routes.cart_url;
              return;
            }

            // Publish cart update — same source as product-form.js so all
            // theme subscribers (cart count, drawer badge, etc.) respond
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            });

            // Render cart contents — this opens the cart drawer or updates
            // the cart notification, exactly like product-form.js does
            cart.renderContents(response);

            // Show success state
            this.classList.remove('is-loading');
            this.classList.add('is-success');
            setTimeout(() => {
              this.classList.remove('is-success');
              this._reset();
            }, 1200);
          })
          .catch((e) => {
            console.error(e);
            this.classList.remove('is-loading');
          })
          .finally(() => {
            if (cart && cart.classList.contains('is-empty')) {
              cart.classList.remove('is-empty');
            }
          });
      }

      _reset() {
        this.selectedOptions = {};
        this.currentStep = 0;
        if (this.optionNames.length > 1) this._showStep(0);
      }
    }
  );
}
